import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import Input from "../common/Input";
import { leadService } from "../../services/leads";
import { teamService } from "../../services/team";
import toast from "react-hot-toast";

const LeadForm = ({ lead, onSuccess }) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: lead || {},
  });
  const [loading, setLoading] = useState(false);

  const { data: teamMembers } = useQuery({
    queryKey: ["team"],
    queryFn: teamService.getAll,
  });

  useEffect(() => {
    reset(lead || {});
  }, [lead, reset]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (lead) {
        await leadService.update(lead._id, data);
        toast.success("Lead updated");
      } else {
        await leadService.create(data);
        toast.success("Lead created");
      }
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      reset();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <Input
        label="Name"
        {...register("name", { required: "Name is required" })}
        error={errors.name?.message}
      />
      <Input
        label="Phone"
        {...register("phone", { required: "Phone is required" })}
        error={errors.phone?.message}
      />
      <Input
        label="Email"
        type="email"
        {...register("email")}
        error={errors.email?.message}
      />
      <Input label="Company" {...register("company")} />
      <Input
        label="Source"
        {...register("source", { required: "Source is required" })}
        error={errors.source?.message}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Status</label>
        <select
          {...register("status")}
          className="w-full border rounded p-2"
        >
          <option value="">Select Status</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="interested">Interested</option>
          <option value="proposal_sent">Proposal Sent</option>
          <option value="converted">Converted</option>
          <option value="lost">Lost</option>
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Assigned To</label>
        <select
          {...register("assignedTo")}
          className="w-full border rounded p-2"
        >
          <option value="">Select Team Member</option>
          {teamMembers?.map((member) => (
            <option key={member._id} value={member._id}>
              {member.name}
            </option>
          ))}
        </select>
      </div>
      <Input
        label="Follow Up Date"
        type="date"
        {...register("followUpDate")}
      />
      <div>
        <label className="block text-sm font-medium mb-1">Lead Score</label>
        <select
          {...register("leadScore")}
          className="w-full border rounded p-2"
        >
          <option value="">Select Score</option>
          <option value="hot">Hot</option>
          <option value="warm">Warm</option>
          <option value="cold">Cold</option>
        </select>
      </div>
      <Button type="submit" disabled={loading}>
        {loading ? "Saving..." : lead ? "Update" : "Create"}
      </Button>
    </form>
  );
};

export default LeadForm;
